import { useTranslation } from 'react-i18next';

import type { CustomQuizQuestion } from '@cfa/pathway-fe-shared/xp';
import { Card, CardContent } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import SixDotIcon from '../../BuildPlans/EditablePlanItems/SixDotIcon';
import type { QuizLocale } from '../state';

interface CollapsedQuestionPreviewProps {
  question: CustomQuizQuestion;
  questionIndex: number;
  /** V2: Settings language from parent. V1: omitted — uses auth profile language. */
  userLanguage?: QuizLocale;
  isHighlighted?: boolean;
  hasError?: boolean;
}

const CollapsedQuestionPreview = ({
  question,
  questionIndex,
  userLanguage: userLanguageProp,
  isHighlighted = false,
  hasError = false,
}: CollapsedQuestionPreviewProps) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const userLanguage: QuizLocale =
    userLanguageProp ?? (auth?.language === 'es' ? 'es' : 'en');
  const questionText = question.question[userLanguage] ?? '';

  return (
    <Card
      elevation={1}
      variant="outlined"
      className={cn(
        'mb-4 cursor-grabbing overflow-hidden! text-left! drop-shadow-lg',
        isHighlighted &&
          'bg-secondary-base! max-h-[70px]! max-w-[400px]! rounded-xl! py-1!',
        hasError && 'border-feedback-error-base! border-2!',
      )}
    >
      <CardContent>
        <div className="flex items-start justify-start gap-2">
          <span className={cn(!isHighlighted && 'opacity-0')}>
            <SixDotIcon position="top" color="white" />
          </span>
          <div className="flex min-w-0 flex-1 items-center">
            {!isHighlighted && (
              <div className="text-brand-navy dark:text-contrast bg-brand-navy/10 dark:bg-tag-navy-container mr-3 flex size-8 items-center justify-center rounded-full text-lg font-bold">
                {questionIndex + 1}
              </div>
            )}
            <p
              className={cn(
                'truncate text-left text-base font-medium',
                isHighlighted && 'text-white',
              )}
            >
              {isHighlighted ? (
                <>
                  <span className="font-bold">
                    {t('Generic.questionNumber', { number: questionIndex + 1 })}
                  </span>
                  <span className="text-sm font-light">
                    {questionText && ` - ${questionText}`}
                  </span>
                </>
              ) : (
                questionText ||
                t('Generic.questionNumber', { number: questionIndex + 1 })
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CollapsedQuestionPreview;
